import { cn } from "@/lib/utils";
import { Empty } from "components/core/Empty";
import { ContentGridMixContentQuery } from "components/domain/contents/ContentGridMixContentQuery";
import { ContentGridQuery } from "components/domain/contents/ContentGridQuery";
import { useAuth } from "hooks/useAuth";
import { useRouter } from "next/router";
import { ComponentProps, useMemo } from "react";
import { ContentSection } from "types/app";
import { LIMIT } from "utils/buildQueryVariables";
import {
    MIX_CONTENT_TAG_ENUM_KOREAN,
    ORDERED_MIX_CONTENT_TAG_ENUM
} from "utils/enum";

import { Section, SectionHeader, SectionTitle } from ".";
import { useMixArchives } from "../hooks/useMixArchives";

export const MixedSection = ({
    contentSection,
    variables,
    limit = LIMIT.default,
    className,
    ...props
}: Omit<
    ComponentProps<
        typeof ContentGridQuery<MixContentQuery, MixContentQueryVariables>
    >,
    "useQuery" | "extractor" | "updateQuery"
> & {
    className?: string;
    contentSection: ContentSection;
    variables: MixContentQueryVariables;
}) => {
    const router = useRouter();
    const { me } = useAuth();
    const isGuest = !me;

    const { archives, archiveTagEnums, archivesLoading } = useMixArchives();

    const tag = useMemo(
        () =>
            ORDERED_MIX_CONTENT_TAG_ENUM.find(
                (tag) => tag === router.query.tag
            ) ?? null,
        [router]
    );

    const tagDisplayName = useMemo(() => {
        // Handle campaign-specific display names
        const campaignTag = router.query.tag;
        // if (campaignTag === "trial_1") {
        //   return "미래에셋증권";
        // }
        if (!tag) return null;
        return MIX_CONTENT_TAG_ENUM_KOREAN[tag];
    }, [tag, router.query.tag]);

    return (
        <Section className={className} {...props}>
            <SectionHeader>
                <SectionTitle>
                    {tag !== null && (
                        <span
                            className="ml-3 hidden text-text-placeholder
                                lg:inline"
                        >
                            #{tagDisplayName}
                        </span>
                    )}
                </SectionTitle>
            </SectionHeader>

            <ContentGridMixContentQuery
                type="mixed"
                contentSection={contentSection}
                limit={limit}
                variables={variables}
                archives={archives}
                archiveTagEnums={archiveTagEnums}
                archivesLoading={archivesLoading}
                isGuest={isGuest}
                empty={
                    <Empty
                        imgurl="/img/content-empty.png"
                        mainText="추천할 만한 콘텐츠를 열심히 찾고 있어요."
                        subText="조금만 기다려 주세요!"
                    />
                }
            />
        </Section>
    );
};
